import React, { useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

/* ---------------------------------
   Country normalization
--------------------------------- */
const COUNTRY_CODE_TO_NAME = {
  US: "United States",
  USA: "United States",
  "UNITED STATES": "United States",
  "UNITED STATES OF AMERICA": "United States",
  CA: "Canada",
  CANADA: "Canada",
};

/* ---------------------------------
   US State normalization
--------------------------------- */
const STATE_CODE_MAP_US = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

/* ---------------------------------
   Reverse map: Name → Code
--------------------------------- */
const STATE_NAME_TO_CODE_US = Object.fromEntries(
  Object.entries(STATE_CODE_MAP_US).map(([code, name]) => [
    name.toUpperCase(),
    code,
  ]),
);

/* ---------------------------------
   State / Province options by country
--------------------------------- */
const STATE_OPTIONS = {
  "United States": Object.values(STATE_CODE_MAP_US),
  Canada: [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Nova Scotia",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
  ],
};

const STATE_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 320,
      "& .MuiMenuItem-root": {
        color: "#00336C",
      },
    },
  },
};

/* ---------------------------------
   Normalizers
--------------------------------- */
const normalizeCountry = (value) => {
  if (!value) return "";
  return COUNTRY_CODE_TO_NAME[value.toString().trim().toUpperCase()] || "";
};

const normalizeStateToLabel = (value) => {
  if (!value) return "";

  const v = value.toString().trim().toUpperCase();

  // Code → Name (NY → New York)
  if (STATE_CODE_MAP_US[v]) return STATE_CODE_MAP_US[v];

  // Name → Proper Name (new york → New York)
  if (STATE_NAME_TO_CODE_US[v]) {
    return STATE_CODE_MAP_US[STATE_NAME_TO_CODE_US[v]];
  }

  // Fallback (international / free-text)
  return value;
};

/* ---------------------------------
   Component
--------------------------------- */
function StateDropdown({
  country,
  value,
  onChange,
  label = "State / Province",
  required = false,
  sx = {},
}) {
  const normalizedCountry = useMemo(() => normalizeCountry(country), [country]);

  const normalizedValue = useMemo(() => normalizeStateToLabel(value), [value]);

  const states = STATE_OPTIONS[normalizedCountry] || [];
  const hasStateList = states.length > 0;

  /* ---------------------------------
     Conditional render
  --------------------------------- */
  if (!hasStateList) return null;

  return (
    <FormControl
      required={required}
      sx={{
        "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
          color: "#00336C",
        },
        "& .MuiSelect-select, & .MuiSelect-icon": {
          color: "#00336C",
        },
        ...sx,
      }}
      fullWidth
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={states.includes(normalizedValue) ? normalizedValue : ""}
        label={label}
        onChange={onChange}
        required={required}
        MenuProps={STATE_MENU_PROPS}
      >
        {states.map((state) => (
          <MenuItem key={state} value={state}>
            {state}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default StateDropdown;
